import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  DefaultButton,
  DetailsList,
  DropdownField,
  FieldArray,
  IColumn,
  IconButton,
  mergeStyleSets,
  Option,
  Stack,
  useFieldArray,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { FormChoiceComboBox } from "@components/form/fields/FormChoiceComboBox";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { ComparisonOperator } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useSelectorDataTypeRefData } from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { getPropertyAndIdentifierOptions } from "@libs/api/gateways/field/field-ops-gateway.utils";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SelectorFormRule, SelectorFormValues } from "./formUtils";
import { SelectorListTypeValueColumn } from "./SelectorListTypeValueColumn";

const QUERY_PARAM_NAME = "query";

// Operator Options
export const operatorOptions = Object.keys(ComparisonOperator).map(x => {
  return { key: ComparisonOperator[x], text: ComparisonOperator[x] };
});

interface SelectorRuleTableProps {
  selectorMode?: boolean;
}

export const SelectorRuleTable = ({ selectorMode }: SelectorRuleTableProps) => {
  const theme = useTheme();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const options =
    selectorMode === true
      ? operatorOptions.filter(x => x.key !== ComparisonOperator.Like)
      : operatorOptions;

  const { data: dataTypes = [] } = useSelectorDataTypeRefData();

  const { actions } = useFormContext<SelectorFormValues>();
  const { value: rules } = useFieldArray<SelectorFormRule>("rules");

  // Get query from URL
  const [encodedQueryString, setEncodedQueryString] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams(
    new URLSearchParams(QUERY_PARAM_NAME)
  );

  // When page is loaded, populate rules from query string if available
  useEffect(() => {
    if (!!encodedQueryString) return;

    const queryArg = searchParams.get(QUERY_PARAM_NAME);
    if (!queryArg) return;

    try {
      const decoded = atob(queryArg);
      const rules = JSON.parse(decoded) as SelectorFormRule[];

      actions.setValue("rules", rules);
    } catch {
      setEncodedQueryString(undefined);
    }
  }, [encodedQueryString, searchParams, actions]);

  // When rules update, set query string
  useEffect(() => {
    if (!rules || rules.every(x => !x.value || !x.attributeType)) return;

    const encoded = btoa(JSON.stringify(rules));

    if (encoded === encodedQueryString) return;

    setEncodedQueryString(encoded);

    const newParams = new URLSearchParams(searchParams);
    newParams.set(QUERY_PARAM_NAME, encoded);
    setSearchParams(newParams);
  }, [
    rules,
    encodedQueryString,
    searchParams,
    setEncodedQueryString,
    setSearchParams
  ]);

  const attributeTypeOptions = getPropertyAndIdentifierOptions();

  const onAdd = async () => {
    actions.arrayMutators.push("rules", {
      attributeType: "",
      value: "",
      comparisonOperator: options[0].key, // default operator is "="
      id: crypto.randomUUID(),
      valueDataType: "STRING"
    });
  };

  const onDelete = (index: number) => {
    actions.arrayMutators.remove("rules", index);
  };

  const ruleValueDataTypeColumn = (name: string) => {
    const ruleValueCol = `${name}.valueDataType`;

    const options = dataTypes?.map(ref => ({
      key: ref.code,
      text: ref.code
    })) as Option[];

    return (
      <DropdownField
        withNoEmptyOption
        name={ruleValueCol}
        options={options}
        fieldItemStyles={{ root: { width: "100%", height: "100%" } }}
      />
    );
  };

  const ruleNameColumn = (name: string) => {
    const ruleValueCol = `${name}.attributeType`;
    return (
      <FormChoiceComboBox name={ruleValueCol} options={attributeTypeOptions} />
    );
  };

  const ruleOperator = (name: string) => {
    const ruleValueCol = `${name}.comparisonOperator`;
    return (
      <DropdownField
        withNoEmptyOption
        name={ruleValueCol}
        options={options}
        fieldItemStyles={{ root: { width: "100%", height: "100%" } }}
      />
    );
  };

  const columns: IColumn[] = [
    {
      key: "name",
      name: "Attribute Name",
      minWidth: 150,
      maxWidth: 300,
      isResizable: true,
      onRender: (name: string) => ruleNameColumn(name)
    },
    {
      key: "operator",
      name: "Operator",
      minWidth: 150,
      maxWidth: 150,
      isResizable: true,
      onRender: (name: string) => ruleOperator(name)
    },
    {
      key: "value",
      name: "Attribute Value",
      minWidth: 150,
      isResizable: true,
      onRender: (name: string, index: number) => (
        <SelectorListTypeValueColumn index={index} name={name} />
      )
    },
    {
      key: "dataType",
      name: "Attribute DataType",
      minWidth: 150,
      maxWidth: 300,
      isResizable: true,
      onRender: (name: string) => ruleValueDataTypeColumn(name)
    },
    {
      key: "delete",
      name: "",
      minWidth: 30,
      maxWidth: 30,
      onRender: (_, index: number) => (
        <Stack.Item styles={{ root: { height: "100%" } }}>
          <IconButton
            onClick={() => {
              onDelete(index);
            }}
            styles={mergeStyleSets(dangerButtonStyles(theme), {
              root: {
                background: "transparent"
              }
            })}
            iconProps={{ iconName: "Cancel" }}
            disabled={!hasWritePermission}
          />
        </Stack.Item>
      )
    }
  ];

  return (
    <>
      <FieldArray name="rules">
        {names => {
          return <DetailsList columns={columns} items={names} />;
        }}
      </FieldArray>

      <DefaultButton
        iconProps={{ iconName: "Add" }}
        styles={{ root: { width: "max-content" } }}
        onClick={onAdd}
        disabled={!hasWritePermission}
      >
        Add rule
      </DefaultButton>
    </>
  );
};
